import {createEvent} from '../../../../../../common/createEvent.function';
import {PagewebError} from '../../../../../../errors/PagewebError.class';
import {ViewerInfo} from '../../../../../../graphql/generated/types';
import {ContentMetaInfo} from '../type/ContentMetaInfo.interface';
import {FrameSize} from '../type/FrameSize.interface';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {LoadedInfo} from '../type/LoadedInfo.interface';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {EventType} from '../type/textViewerSagaEventType.enum';

export const events = {
  textViewerEntered: ({
    seriesId,
    productId,
    isPreview,
    viewerInfoData,
  }: {
    seriesId: number;
    productId: number;
    isPreview?: boolean;
    viewerInfoData: ViewerInfo;
  }) =>
    createEvent(EventType.TextViewerEntered, {
      seriesId,
      productId,
      isPreview,
      viewerInfoData,
    }),
  textViewerDataLoadRequired: ({
    seriesId,
    productId,
    viewerInfoData,
  }: {
    seriesId: number;
    productId: number;
    viewerInfoData: ViewerInfo;
  }) =>
    createEvent(EventType.TextViewerDataLoadRequired, {
      seriesId,
      productId,
      viewerInfoData,
    }),
  textViewerDataLoadCompleted: ({productId}: {productId: number}) =>
    createEvent(EventType.TextViewerDataLoadCompleted, {
      productId,
    }),
  textViewerMetaLoadCompleted: ({
    productId,
    metaInfo,
    loadedInfo,
  }: {
    productId: number;
    metaInfo?: ContentMetaInfo | undefined;
    loadedInfo?: LoadedInfo | undefined;
  }) =>
    createEvent(EventType.TextViewerMetaLoadCompleted, {
      productId,
      metaInfo,
      loadedInfo,
    }),
  textViewerPreloadCompleted: ({
    productId,
    chapterId,
    htmlInfo,
  }: {
    productId: number;
    chapterId: number;
    htmlInfo: HtmlInfo | undefined;
  }) =>
    createEvent(EventType.TextViewerPreloadCompleted, {
      productId,
      chapterId,
      htmlInfo,
    }),
  textViewerChapterLoadCompleted: ({
    productId,
    chapterId,
    htmlInfo,
  }: {
    productId: number;
    chapterId: number;
    htmlInfo: HtmlInfo | undefined;
  }) =>
    createEvent(EventType.TextViewerChapterLoadCompleted, {
      productId,
      chapterId,
      htmlInfo,
    }),
  currentChapterChanged: ({productId}: {productId: number}) =>
    createEvent(EventType.CurrentChapterChanged, {productId}),
  textViewerContentLoadRequired: ({productId}: {productId: number}) =>
    createEvent(EventType.TextViewerContentLoadRequired, {productId}),
  textViewerContentLoadCompleted: ({
    productId,
    wrapperStartY,
    wrapperBottomY,
  }: {
    productId: number;
    wrapperStartY?: number;
    wrapperBottomY?: number;
  }) =>
    createEvent(EventType.TextViewerContentLoadCompleted, {
      productId,
      wrapperStartY,
      wrapperBottomY,
    }),
  currentPageCountRequired: ({
    productId,
    size,
    isPreview,
  }: {
    productId: number;
    size: FrameSize;
    isPreview?: boolean;
  }) =>
    createEvent(EventType.CurrentPageCountRequired, {
      productId,
      size,
      isPreview,
    }),
  allPageCountRequired: ({
    productId,
    size,
    isPreview,
  }: {
    productId: number;
    size: FrameSize;
    isPreview?: boolean;
  }) =>
    createEvent(EventType.AllPageCountRequired, {productId, size, isPreview}),
  allPageCountCompleted: () => createEvent(EventType.AllPageCountCompleted),
  redrawContentRequired: ({productId}: {productId: number}) =>
    createEvent(EventType.RedrawContentRequired, {productId}),
  redrawContentCompleted: ({
    productId,
    readType,
    size,
    wrapperStartY,
    wrapperBottomY,
    needScrollAdjust,
    isPreview,
  }:
    | {
        productId: number;
        readType: TextViewerReadType.Page;
        size?: never;
        wrapperStartY?: never;
        wrapperBottomY?: never;
        needScrollAdjust?: boolean;
        isPreview?: boolean;
      }
    | {
        productId: number;
        readType: TextViewerReadType.Scroll;
        size: FrameSize;
        wrapperStartY: number;
        wrapperBottomY: number;
        needScrollAdjust?: boolean;
        isPreview?: boolean;
      }) =>
    createEvent(EventType.RedrawContentCompleted, {
      productId,
      readType,
      size,
      wrapperStartY,
      wrapperBottomY,
      needScrollAdjust,
      isPreview,
    }),
  applyScrollViewerRateRequired: ({
    productId,
    wrapperStartY,
    wrapperBottomY,
  }: {
    productId: number;
    wrapperStartY: number;
    wrapperBottomY: number;
  }) =>
    createEvent(EventType.ApplyScrollViewerRateRequired, {
      productId,
      wrapperStartY,
      wrapperBottomY,
    }),
  applyScrollViewerRateCompleted: ({
    productId,
    wrapperStartY,
    wrapperBottomY,
  }: {
    productId: number;
    wrapperStartY: number;
    wrapperBottomY: number;
  }) =>
    createEvent(EventType.ApplyScrollViewerRateCompleted, {
      productId,
      wrapperStartY,
      wrapperBottomY,
    }),
  applyPageViewerRateRequired: ({productId}: {productId: number}) =>
    createEvent(EventType.ApplyPageViewerRateRequired, {productId}),
  applyPageViewerRateCompleted: ({productId}: {productId: number}) =>
    createEvent(EventType.ApplyPageViewerRateCompleted, {productId}),
  updateScrollViewerCurrentRateRequired: ({
    productId,
    wrapperStartY,
    wrapperBottomY,
  }: {
    productId: number;
    wrapperStartY: number;
    wrapperBottomY: number;
  }) =>
    createEvent(EventType.UpdateScrollViewerCurrentRateRequired, {
      productId,
      wrapperStartY,
      wrapperBottomY,
    }),
  updatePageViewerCurrentRateRequired: ({productId}: {productId: number}) =>
    createEvent(EventType.UpdatePageViewerCurrentRateRequired, {productId}),
  textViewerErrorOccurred: ({
    productId,
    error,
  }: {
    productId: number;
    error: PagewebError;
  }) => createEvent(EventType.TextViewerErrorOccurred, {productId, error}),
};
